import { useEffect, useMemo, useState } from 'react';

import { useConfigSlice } from '../../../store/slices/config/selectors';
import { AppManifest } from '../../appManifest/AppManifest';
import { FeatureService, FeatureServiceState } from '../FeatureService';
import { JSONFetcher } from '../fetchers/JSONFetcher';
import { PMSFetcher } from '../fetchers/PMSFetcher';

/**
 * Hook to initialise the FeatureService.
 *
 * To be used only once.
 * */
export const useFeatureServiceConnected = (featureConfig?: string | string[]): ReturnType<typeof useFeatureService> => {
  const JSESSIONID = useConfigSlice.JSESSIONID();
  const baseUrl = useConfigSlice.baseUrl();
  const casinoId = useConfigSlice.session()?.casinoId;

  return useFeatureService(JSESSIONID, baseUrl, casinoId, featureConfig);
};

export const useFeatureService = (
  JSESSIONID: string | null,
  baseUrl: string | null,
  casinoId: string | undefined,
  featureConfig?: string | string[]
): { isReady: boolean } => {
  const isReady = useFeatureServiceReady();

  useEffect(() => {
    if (JSESSIONID && baseUrl && casinoId) {
      const fetcher = AppManifest.getFlag('usePMS')
        ? new PMSFetcher(JSESSIONID, baseUrl)
        : new JSONFetcher(casinoId, featureConfig);
      FeatureService.init(fetcher);
    }
  }, [JSESSIONID, baseUrl, featureConfig]);

  return useMemo(() => {
    return {
      isReady,
    };
  }, [isReady]);
};

export const useFeatureServiceReady = (): boolean => {
  const [ready, setReady] = useState<boolean>(FeatureService.state === FeatureServiceState.READY);

  useEffect(() => {
    return FeatureService.addEventListener('ready', (): void => {
      setReady(FeatureService.state === FeatureServiceState.READY);
    });
  }, []);

  return ready;
};

export const useFeature = (name: string): boolean => {
  const [feature, setFeature] = useState<boolean>(FeatureService.getFeature(name) ?? false);

  useEffect(() => {
    setFeature(FeatureService.getFeature(name) ?? false);
    return FeatureService.addEventListener('updated', (): void => {
      setFeature(FeatureService.getFeature(name) ?? false);
    });
  }, [name]);

  return feature;
};
